import { Tabs } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import ClockInHistory from "./ClockInHistory";
import Allowance from "./Allowance";
import Calendar from "./Calendar";
import { PagePermissionNameData } from "../../../helpers/permissionModule";
import ClockInTimeOffHistory from "./ClockInTimeOffHistory";
import { getDecryptedUserData } from "../../../helpers/bycrypt";
import PtoHistory from "../TeamMembers/PtoHistory";
import { svg } from "../../../Assets/svg";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import NotificationButton from "../../Components/NotificationButton";
import { GetUnreadNotificationCountAPI } from "../../Redux/notification";
import { useIsTimeVariant } from "../../../helpers/appPackage";
import { getBusinessCurrentYear } from "../../../helpers/utils";
import dayjs from "dayjs";

export enum ClockInHistoryTabsEnum {
  MY_ATTENDANCE = "my-attendance",
  PENDING_ALLOWANCE = "pending-allowance",
  TIME_OFF_HISTORY = "time-off-history",
  PTO_HISTORY = "pto-history",
  CALENDAR = "calendar",
}

const normalizeTab = (val?: string | null): ClockInHistoryTabsEnum => {
  if (!val) return ClockInHistoryTabsEnum.MY_ATTENDANCE;
  const lower = val.toLowerCase();
  if (lower === "my-attendance" || lower === "attendance") {
    return ClockInHistoryTabsEnum.MY_ATTENDANCE;
  }
  if (lower === "pending-allowance" || lower === "allowance") {
    return ClockInHistoryTabsEnum.PENDING_ALLOWANCE;
  }
  if (lower === "time-off-history" || lower === "time-off" || lower === "time_off") {
    return ClockInHistoryTabsEnum.TIME_OFF_HISTORY;
  }
  if (lower === "calendar") {
    return ClockInHistoryTabsEnum.CALENDAR;
  }
  if (lower === "pto-history" || lower === "pto") {
    return ClockInHistoryTabsEnum.PTO_HISTORY;
  }
  return ClockInHistoryTabsEnum.MY_ATTENDANCE;
};

const tabSupportsYear = (tab: string): boolean => {
  return (
    tab === ClockInHistoryTabsEnum.PENDING_ALLOWANCE ||
    tab === ClockInHistoryTabsEnum.TIME_OFF_HISTORY ||
    tab === ClockInHistoryTabsEnum.CALENDAR ||
    tab === ClockInHistoryTabsEnum.PTO_HISTORY
  );
};

const ClockInHistoryWrapper = () => {
  const user = getDecryptedUserData();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const isTimeVariant = useIsTimeVariant();

  useEffect(() => {
    if (isTimeVariant) {
      dispatch(GetUnreadNotificationCountAPI({ app: "time" }) as any);
    }
  }, [isTimeVariant, dispatch]);

  const getInitialTab = (): ClockInHistoryTabsEnum => {
    const qsTab = searchParams.get("tab");
    if (qsTab) {
      return normalizeTab(qsTab);
    }
    const stateTab = (location.state as any)?.activeTab;
    if (stateTab) {
      return normalizeTab(stateTab);
    }
    if ((location.state as any)?.year || (location.state as any)?.selectedYear) {
      return ClockInHistoryTabsEnum.TIME_OFF_HISTORY;
    }
    if (location.search.includes("time-off")) {
      return ClockInHistoryTabsEnum.TIME_OFF_HISTORY;
    }
    if (searchParams.has("year")) {
      return ClockInHistoryTabsEnum.TIME_OFF_HISTORY;
    }
    const saved = sessionStorage.getItem("clock_in_history_active_tab");
    if (saved) {
      return normalizeTab(saved);
    }
    return ClockInHistoryTabsEnum.MY_ATTENDANCE;
  };

  const [activeView, setActiveView] = useState<string>(getInitialTab);

  const handleTabChange = (key: string) => {
    const nextTab = normalizeTab(key);
    setActiveView(nextTab);
    sessionStorage.setItem("clock_in_history_active_tab", nextTab);

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", nextTab);
        if (!tabSupportsYear(nextTab)) {
          next.delete("year");
        } else if (!next.has("year")) {
          let defaultYear = "";
          if (nextTab === ClockInHistoryTabsEnum.PENDING_ALLOWANCE) {
            defaultYear = String(getBusinessCurrentYear());
          } else if (nextTab === ClockInHistoryTabsEnum.TIME_OFF_HISTORY) {
            const saved = sessionStorage.getItem("clock_in_time_off_history_year");
            defaultYear = saved && saved !== "all" ? saved : String(getBusinessCurrentYear());
          } else if (nextTab === ClockInHistoryTabsEnum.CALENDAR) {
            defaultYear = String(dayjs().year());
          }
          if (defaultYear) {
            next.set("year", defaultYear);
          }
        }
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    const currentTabParam = searchParams.get("tab");
    const normalizedParamTab = currentTabParam ? normalizeTab(currentTabParam) : null;

    if (normalizedParamTab && normalizedParamTab !== activeView) {
      setActiveView(normalizedParamTab);
      sessionStorage.setItem("clock_in_history_active_tab", normalizedParamTab);
    }

    const targetTab = normalizedParamTab || activeView;

    let needsUpdate = false;
    const next = new URLSearchParams(searchParams);

    if (next.get("tab") !== targetTab) {
      next.set("tab", targetTab);
      needsUpdate = true;
    }

    if (!tabSupportsYear(targetTab)) {
      if (next.has("year")) {
        next.delete("year");
        needsUpdate = true;
      }
    } else {
      if (!next.has("year")) {
        let defaultYear = "";
        if (targetTab === ClockInHistoryTabsEnum.PENDING_ALLOWANCE) {
          defaultYear = String(getBusinessCurrentYear());
        } else if (targetTab === ClockInHistoryTabsEnum.TIME_OFF_HISTORY) {
          const saved = sessionStorage.getItem("clock_in_time_off_history_year");
          defaultYear = saved && saved !== "all" ? saved : String(getBusinessCurrentYear());
        } else if (targetTab === ClockInHistoryTabsEnum.CALENDAR) {
          defaultYear = String(dayjs().year());
        }
        if (defaultYear) {
          next.set("year", defaultYear);
          needsUpdate = true;
        }
      }
    }

    if (needsUpdate) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, activeView, setSearchParams]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const items = useMemo(() => {
    return [
      {
        label: `Attendance`,
        key: ClockInHistoryTabsEnum.MY_ATTENDANCE,
        icon: svg?.clockInHistoryTabIcons?.attendance,
      },
      {
        label: `Allowance`,
        key: ClockInHistoryTabsEnum.PENDING_ALLOWANCE,
        icon: svg?.clockInHistoryTabIcons?.allowance,
      },
      {
        label: `Time Off`,
        key: ClockInHistoryTabsEnum.TIME_OFF_HISTORY,
        icon: svg?.clockInHistoryTabIcons?.timeOff,
      },
      // {
      //   label: `PTO History`,
      //   key: ClockInHistoryTabsEnum.PTO_HISTORY,
      // },
      // {
      //   label: `Calendar`,
      //   key: ClockInHistoryTabsEnum.CALENDAR,
      //   icon: svg?.clockInHistoryTabIcons?.calendar,
      // },
    ];
  }, []);

  return (
    <div className="page-content h-100 py-40 pb-0 mobile-font-s d-flex flex-column overflow-hidden">
      <div className={"d-flex justify-content-between align-items-center mb-md-3 mb-2 px-40 gap-2"}>
        <div className="customHorizontalTabs clock-in-history-tabs bg-white overflow-hidden border-0 rounded-3 d-inline-block mw-100">
          <Tabs
            // tabPosition={position}
            activeKey={activeView}
            onChange={(key) => {
              handleTabChange(key);
            }}
            items={items}
          />
        </div>
        {!isMobile && (
          <div className="d-flex align-items-center gap-2">
            <div className="cale-list-btn">
              <button
                className={`btn d-flex align-items-center justify-content-center p-2 rounded-3 ${activeView === ClockInHistoryTabsEnum.CALENDAR
                  ? "btn-primary border-0"
                  : "bg-transparent"
                  }`}
                style={{
                  border:
                    activeView === ClockInHistoryTabsEnum.CALENDAR
                      ? "none"
                      : "1.5px solid #231f20",
                }}
                onClick={() => {
                  handleTabChange(ClockInHistoryTabsEnum.CALENDAR);
                }}
              >
                <span
                  className="svg-20 d-flex"
                  style={{
                    filter:
                      activeView === ClockInHistoryTabsEnum.CALENDAR
                        ? "brightness(0) invert(1)"
                        : "none",
                  }}
                >
                  {svg?.clockInHistoryTabIcons?.calendar}
                </span>
              </button>
            </div>
            <NotificationButton />
          </div>
        )}
      </div>
      {/* <div className="d-inline-flex align-items-center bg-white rounded-5 mb-2">
            {items?.map((i, index) => {

              return (
                <div className={`d-flex align-items-center px-2 py-2 fs-5 text-nowrap user-select-none ${index === selectedIndex ? 'clock-in-tab-selected-item rounded-5 text-white' : ''}`} onClick={() => setSelectedIndex(index)}>
                  <span className="custom-tab-icon me-2 d-flex align-items-center">{i.icon}</span>
                  {i.label}

                </div>
              )
            })}
          </div> */}
      <div className="flex-fill overflow-hidden d-flex flex-column">
        {activeView === ClockInHistoryTabsEnum.MY_ATTENDANCE && (
          <div className="h-100 overflow-y-hidden d-flex flex-column px-40 pb-3">
            <ClockInHistory isPersonalHistory={true} />
          </div>
        )}

        {activeView === ClockInHistoryTabsEnum.PENDING_ALLOWANCE && (
          <div className="h-100 overflow-y-auto px-40 pb-3">
            <Allowance
              pagePermission={PagePermissionNameData?.CLOCK_IN_HISTORY}
              setActiveView={handleTabChange}
            />
          </div>
        )}

        {activeView === ClockInHistoryTabsEnum.TIME_OFF_HISTORY && (
          <div className="h-100 overflow-y-auto px-40 pb-3 d-flex flex-column flex-fill">
            <ClockInTimeOffHistory id={user?._id} />
          </div>
        )}

        {activeView === ClockInHistoryTabsEnum.PTO_HISTORY && (
          <div className="h-100 overflow-y-auto px-40 pb-3">
            <PtoHistory
              pagePermission={PagePermissionNameData?.CLOCK_IN_HISTORY}
              isEmbedded={true}
            />
          </div>
        )}

        {activeView === ClockInHistoryTabsEnum.CALENDAR && (
          <div className="d-flex flex-column h-100 overflow-y-auto px-40 pb-3">
            <Calendar isTab={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClockInHistoryWrapper;
